import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { getNote } from '@/common/notes'
import { DBNote, DBRecipient } from 'types/custom-db'
import { listRecipients } from '@/common/recipients'
import { addRealTimeListener } from '@/common/realtime'
import { setCurrentTheme } from '@/common/utils'

export type SingleNote = DBNote | null
export type SingleRecipient = DBRecipient | null
export type MultipleRecipients = SingleRecipient[] | null
type NoteDetail = {
  note_id: string
  note: SingleNote
  recipients: MultipleRecipients
}

const noteContext = createContext<NoteDetail>({
  note: null,
  recipients: null,
  note_id: '',
})

function useProvideNote(note_id: string): NoteDetail {
  const [note, setNote] = useState<SingleNote>(null)
  const [recipients, setRecipients] = useState<MultipleRecipients>(null)
  const fetchData = useCallback(() => {
    // TODO, create a single query
    getNote(note_id).then((response) => {
      if (response.ok) {
        setNote(response.data?.[0])
      }
    })

    listRecipients(note_id).then((response) => {
      if (response.ok) {
        setRecipients(response.data)
      }
    })
  }, [note_id])

  useEffect(() => {
    if (!note_id) return

    fetchData() // init
    const recipientsListener = addRealTimeListener({
      table: 'recipients',
      callback: fetchData, // whatever the event, just re-fetch the data
    })

    return () => {
      recipientsListener.unsubscribe()
    }
  }, [note_id])

  return {
    note: note,
    note_id: note_id,
    recipients: recipients,
  }
}

export function ProvideNote({
  children,
  note_id,
}: React.PropsWithChildren & {
  note_id: string
}) {
  const noteDetails = useProvideNote(note_id)
  useEffect(setCurrentTheme, [])

  return (
    <noteContext.Provider value={noteDetails}>{children}</noteContext.Provider>
  )
}

export const useNote = () => {
  return useContext(noteContext)
}
